import { compose } from "react-apollo"
import { connect } from "react-redux"
import moment from "moment"
import gql from "graphql-tag"
import { graphqlDynamic } from "react-apollo-dynamic-query"

import { GRAPHQL_DATE_FORMAT } from "../../helpers/sharedConstants"

import UtilizationRangeFilter from "../UtilizationRangeFilter"
import UtilizationUnitDetailTable from "./"

const generateQuery = ({ entityType, timeslice }) => gql`
query AssetUtilizationUnitDetails($entityId: ID!, $deviceTypeId: ID!, $startAt: String!, $endAt: String!, $owningDeptId: ID) {
    entity: ${entityType}(id: $entityId) {
      id
      units {
        id
        name
        deviceType(id: $deviceTypeId) {
          utilization(startAt: $startAt, endAt: $endAt, owningDeptId: $owningDeptId) {
            timeslices {
              ${timeslice}s {
                startsAt
                aggregates {
                  maxInEntity {
                    inEntityCount
                  }
                  peakUtilization {
                    utilizedCount
                  }
                }
              }
              full {
                aggregates {
                  maxInEntity {
                    inEntityCount
                  }
                  peakUtilization {
                    availableCount
                  }
                  averages {
                    utilizedPercentage
                    utilizedCount
                  }
                }
              }
            }
          }
        }
      }
    }
}
  `

// Sort and filter data based on the table sort, UtilizationRangeFilter, and search input.
const sortAndFilter = (
  data,
  { sortBy, sortDirection, filters, searchValue }
) => {
  let processed = data.filter(row => {
    return (
      row.avgUsage > 0 ||
      row.averageUtilizedCount > 0 ||
      row.availableDuringPeak > 0
    )
  })
  processed = processed.sort((row1, row2) => {
    const sortVal1 =
      typeof row1[sortBy] === "string"
        ? row1[sortBy].toLowerCase()
        : row1[sortBy]
    const sortVal2 =
      typeof row2[sortBy] === "string"
        ? row2[sortBy].toLowerCase()
        : row2[sortBy]

    if (sortVal1 > sortVal2) {
      return sortDirection === "ASC" ? 1 : -1
    } else {
      return sortDirection === "ASC" ? -1 : 1
    }
  })

  if (filters[0] !== "all") {
    processed = processed.filter(row =>
      // Go through each of the selected filters and keep this row if it falls within any specified range.
      filters.reduce(
        (included, filter) =>
          included ||
          (row.avgUsage >= UtilizationRangeFilter.filterRanges.get(filter)[0] &&
            row.avgUsage <= UtilizationRangeFilter.filterRanges.get(filter)[1]),
        false // The "included" argument above will start as false.
      )
    )
  }

  if (searchValue !== "") {
    processed = processed.filter(row =>
      row.unitName.toLowerCase().includes(searchValue.toLowerCase())
    )
  }

  return processed
}

export default compose(
  connect(({ dateRange: { startDate, endDate } }) => {
    const start = moment(startDate)
    const end = moment(endDate)
    return {
      startDate: start,
      endDate: end,
      timeslice: end.diff(start, "day") > 92 ? "week" : "day", // 3 months will have at most 92 days. If more than 3 months, slice data weekly.
    }
  }),
  graphqlDynamic(generateQuery, {
    skip: ({ loading }) => loading,
    props: ({ data, ownProps }) => {
      if (data.loading) {
        return { loading: true }
      } else {
        return {
          data: sortAndFilter(
            data.entity.units
              .map(unit => {
                if (
                  unit &&
                  unit.deviceType &&
                  unit.deviceType.utilization &&
                  unit.deviceType.utilization.timeslices.full.aggregates
                ) {
                  return {
                    unitName: unit.name,
                    deviceName: ownProps.deviceName,
                    availableDuringPeak:
                      unit.deviceType.utilization.timeslices.full.aggregates
                        .peakUtilization.availableCount,
                    avgUsage:
                      unit.deviceType.utilization.timeslices.full.aggregates
                        .averages.utilizedPercentage,
                    maxInEntity:
                      unit.deviceType.utilization.timeslices.full.aggregates
                        .maxInEntity.inEntityCount,
                    averageUtilizedCount:
                      unit.deviceType.utilization.timeslices.full.aggregates
                        .averages.utilizedCount,
                    peakUsageTrend: unit.deviceType.utilization.timeslices[
                      `${ownProps.timeslice}s`
                    ]
                      .map(day => {
                        if (day.aggregates) {
                          return {
                            startsAt: day.startsAt,
                            period: ownProps.timeslice,
                            // Even though the field below is called "peakUtilizedCount", we actually want the spark lines to be a graph of peak utilized percentages, hence the math.
                            peakUtilizedCount: Math.round(
                              (day.aggregates.peakUtilization.utilizedCount /
                                day.aggregates.maxInEntity.inEntityCount) *
                                100
                            ),
                          }
                        } else {
                          return null
                        }
                      })
                      .filter(day => day != null),
                  }
                } else {
                  return null
                }
              })
              .filter(dt => dt != null),
            ownProps
          ),
        }
      }
    },
    options: ({
      entityId,
      owningDeptId,
      deviceTypeId,
      startDate,
      endDate,
    }) => ({
      variables: {
        entityId,
        deviceTypeId,
        owningDeptId,
        startAt: startDate.format(GRAPHQL_DATE_FORMAT),
        endAt: endDate.format(GRAPHQL_DATE_FORMAT),
      },
    }),
  })
)(UtilizationUnitDetailTable)
